/**
 * @fileoverview Evaluation Workspace Store
 * @description Persists projects, their test cycles, scenarios, and runs in the
 *              browser. Reports are never sent anywhere by this module.
 * @module features/AIEvaluationResults/utils/evaluationStore
 */

import { evaluatorKey } from './evaluationView';

const STORAGE_KEY = 'aier.workspace.v1';
const MAX_RUNS_PER_SCENARIO = 25;
const MAX_DETAIL_LENGTH = 400;
const MAX_MESSAGES = 12;
const MAX_MESSAGE_LENGTH = 1500;
const MAX_FACTS = 12;
const MAX_QUOTE_LENGTH = 300;

export const PROJECT_COLORS = [
  '#01426A', '#0097A9', '#7C3AED', '#008653',
  '#D97706', '#C81E3A', '#2563EB', '#0F766E',
];

/**
 * Create an identifier for a stored record.
 * @returns {string} Unique identifier.
 */
function createId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${performance.now().toString(36).replace('.', '')}`;
}

/**
 * Move pre-cycle projects onto the project/cycle/scenario shape.
 * @param {Object} project - Stored project.
 * @returns {Object} Project containing cycles.
 */
function migrateProject(project) {
  if (Array.isArray(project.cycles)) {
    return project;
  }
  const { scenarios = [], ...rest } = project;
  return {
    ...rest,
    cycles: scenarios.length
      ? [{
        id: createId(),
        name: 'Cycle 1',
        description: '',
        createdAt: project.createdAt || new Date().toISOString(),
        scenarios,
      }]
      : [],
  };
}

/**
 * Read the stored workspace.
 * @returns {Array<Object>} Projects.
 */
export function loadProjects() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.map(migrateProject) : [];
  } catch {
    return [];
  }
}

/**
 * Write the workspace, ignoring quota failures.
 * @param {Array<Object>} projects - Projects to persist.
 * @returns {Array<Object>} The same projects.
 */
function persist(projects) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
  } catch {
    // Storage is a convenience; a full quota must not break the module.
  }
  return projects;
}

/**
 * Apply a change to one project.
 * @param {string} projectId - Target project.
 * @param {Function} updater - Receives the project and returns the next value.
 * @returns {Array<Object>} Updated projects.
 */
function updateProjectById(projectId, updater) {
  return persist(loadProjects().map((project) => (
    project.id === projectId ? { ...updater(project), updatedAt: new Date().toISOString() } : project
  )));
}

/**
 * Apply a change to one cycle.
 * @param {string} projectId - Target project.
 * @param {string} cycleId - Target cycle.
 * @param {Function} updater - Receives the cycle and returns the next value.
 * @returns {Array<Object>} Updated projects.
 */
function updateCycleById(projectId, cycleId, updater) {
  return updateProjectById(projectId, (project) => ({
    ...project,
    cycles: project.cycles.map((cycle) => (cycle.id === cycleId ? updater(cycle) : cycle)),
  }));
}

/**
 * Create a project.
 * @param {{name: string, description?: string}} input - Project details.
 * @returns {Array<Object>} Updated projects.
 */
export function createProject({ name, description = '', externalId = null }) {
  const projects = loadProjects();
  const project = {
    id: createId(),
    externalId,
    name: name.trim(),
    description: description.trim(),
    color: PROJECT_COLORS[projects.length % PROJECT_COLORS.length],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    cycles: [],
  };
  return persist([...projects, project]);
}

/**
 * Rename or re-describe a project.
 * @param {string} projectId - Target project.
 * @param {Object} patch - Fields to merge.
 * @returns {Array<Object>} Updated projects.
 */
export function updateProject(projectId, patch) {
  return updateProjectById(projectId, (project) => ({ ...project, ...patch }));
}

/**
 * Delete a project and everything inside it.
 * @param {string} projectId - Target project.
 * @returns {Array<Object>} Updated projects.
 */
export function deleteProject(projectId) {
  return persist(loadProjects().filter((project) => project.id !== projectId));
}

/**
 * Create a test cycle inside a project.
 * @param {string} projectId - Target project.
 * @param {{name: string, description?: string}} input - Cycle details.
 * @returns {Array<Object>} Updated projects.
 */
export function createCycle(projectId, { name, description = '' }) {
  return updateProjectById(projectId, (project) => ({
    ...project,
    cycles: [...project.cycles, {
      id: createId(),
      name: name.trim(),
      description: description.trim(),
      createdAt: new Date().toISOString(),
      scenarios: [],
    }],
  }));
}

/**
 * Update a test cycle's details.
 * @param {string} projectId - Target project.
 * @param {string} cycleId - Target cycle.
 * @param {Object} patch - Fields to merge.
 * @returns {Array<Object>} Updated projects.
 */
export function updateCycle(projectId, cycleId, patch) {
  return updateCycleById(projectId, cycleId, (cycle) => ({ ...cycle, ...patch }));
}

/**
 * Delete a test cycle and its scenarios.
 * @param {string} projectId - Target project.
 * @param {string} cycleId - Target cycle.
 * @returns {Array<Object>} Updated projects.
 */
export function deleteCycle(projectId, cycleId) {
  return updateProjectById(projectId, (project) => ({
    ...project,
    cycles: project.cycles.filter((cycle) => cycle.id !== cycleId),
  }));
}

/**
 * Create a scenario inside a test cycle.
 * @param {string} projectId - Target project.
 * @param {string} cycleId - Target cycle.
 * @param {{name: string, description?: string}} input - Scenario details.
 * @returns {Array<Object>} Updated projects.
 */
export function createScenario(projectId, cycleId, { name, description = '', externalId = null }) {
  return updateCycleById(projectId, cycleId, (cycle) => ({
    ...cycle,
    scenarios: [...cycle.scenarios, {
      id: createId(),
      externalId,
      name: name.trim(),
      description: description.trim(),
      createdAt: new Date().toISOString(),
      runs: [],
    }],
  }));
}

/**
 * Update a scenario's details.
 * @param {string} projectId - Target project.
 * @param {string} cycleId - Target cycle.
 * @param {string} scenarioId - Target scenario.
 * @param {Object} patch - Fields to merge.
 * @returns {Array<Object>} Updated projects.
 */
export function updateScenario(projectId, cycleId, scenarioId, patch) {
  return updateCycleById(projectId, cycleId, (cycle) => ({
    ...cycle,
    scenarios: cycle.scenarios.map((scenario) => (
      scenario.id === scenarioId ? { ...scenario, ...patch } : scenario
    )),
  }));
}

/**
 * Delete a scenario and its runs.
 * @param {string} projectId - Target project.
 * @param {string} cycleId - Target cycle.
 * @param {string} scenarioId - Target scenario.
 * @returns {Array<Object>} Updated projects.
 */
export function deleteScenario(projectId, cycleId, scenarioId) {
  return updateCycleById(projectId, cycleId, (cycle) => ({
    ...cycle,
    scenarios: cycle.scenarios.filter((scenario) => scenario.id !== scenarioId),
  }));
}

/**
 * Trim a fact list so stored transcripts stay inside the storage quota.
 * @param {Array<Object>} facts - Evaluated facts.
 * @returns {Array<Object>} Compacted facts.
 */
function compactFacts(facts) {
  return (facts || []).slice(0, MAX_FACTS).map((fact) => ({
    id: fact.id,
    description: fact.description,
    status: fact.status,
    reason: fact.reason,
    evidence: (fact.evidence || []).map((item) => ({
      agentTurn: item.agentTurn,
      quote: String(item.quote || '').slice(0, MAX_QUOTE_LENGTH),
    })),
  }));
}

/**
 * Trim scenario conversations before persisting them.
 * @param {Object} conversations - Conversations keyed by scenario id.
 * @returns {Object} Compacted conversations.
 */
function compactConversations(conversations) {
  return Object.fromEntries(Object.entries(conversations || {}).map(([scenarioId, conversation]) => [
    scenarioId,
    {
      ...conversation,
      coveredFacts: compactFacts(conversation.coveredFacts),
      missingFacts: compactFacts(conversation.missingFacts),
      responses: (conversation.responses || []).slice(0, MAX_MESSAGES).map((message) => ({
        role: message.role,
        text: String(message.text || '').slice(0, MAX_MESSAGE_LENGTH),
        links: message.links || [],
      })),
    },
  ]));
}

/**
 * Reduce an evaluator view to the fields worth persisting.
 * @param {Object} view - Evaluator view of a parsed report.
 * @returns {Object} Storable run record.
 */
function toStoredRun(view) {
  return {
    id: createId(),
    externalId: view.meta?.runExternalId || null,
    label: view.fileName,
    title: view.title,
    description: view.description,
    generatedAt: view.generatedAt || null,
    importedAt: new Date().toISOString(),
    sources: view.sources || [],
    summary: view.summary,
    scenarioLayout: view.format === 'scenario',
    conversations: compactConversations(view.conversations),
    evaluators: view.evaluators.map((row) => ({
      id: row.id,
      key: evaluatorKey(row),
      report: row.report,
      field: row.field,
      evaluator: row.evaluator,
      framework: row.framework,
      approach: row.approach,
      score: row.score,
      band: row.band,
      metrics: row.metrics || null,
      passed: row.passed ?? null,
      turns: row.turns ?? null,
      detail: typeof row.detail === 'string' ? row.detail.slice(0, MAX_DETAIL_LENGTH) : null,
    })),
  };
}

/**
 * Add a run to a scenario.
 * @param {string} projectId - Target project.
 * @param {string} cycleId - Target cycle.
 * @param {string} scenarioId - Target scenario.
 * @param {Object} view - Evaluator view to capture.
 * @returns {{projects: Array<Object>, run: Object}} Updated workspace and stored run.
 */
export function addRun(projectId, cycleId, scenarioId, view) {
  const run = toStoredRun(view);
  const projects = updateCycleById(projectId, cycleId, (cycle) => ({
    ...cycle,
    scenarios: cycle.scenarios.map((scenario) => {
      if (scenario.id !== scenarioId) {
        return scenario;
      }
      // Re-adding the same source run replaces it instead of duplicating.
      const retained = run.externalId
        ? scenario.runs.filter((existing) => existing.externalId !== run.externalId)
        : scenario.runs;
      return { ...scenario, runs: [...retained, run].slice(-MAX_RUNS_PER_SCENARIO) };
    }),
  }));
  return { projects, run };
}

/**
 * Delete a run from a scenario.
 * @param {string} projectId - Target project.
 * @param {string} cycleId - Target cycle.
 * @param {string} scenarioId - Target scenario.
 * @param {string} runId - Target run.
 * @returns {Array<Object>} Updated projects.
 */
export function deleteRun(projectId, cycleId, scenarioId, runId) {
  return updateCycleById(projectId, cycleId, (cycle) => ({
    ...cycle,
    scenarios: cycle.scenarios.map((scenario) => (
      scenario.id === scenarioId
        ? { ...scenario, runs: scenario.runs.filter((run) => run.id !== runId) }
        : scenario
    )),
  }));
}

/**
 * Compare two names ignoring case and surrounding whitespace.
 * @param {string} left - First name.
 * @param {string} right - Second name.
 * @returns {boolean} Whether the names match.
 */
function sameName(left, right) {
  return Boolean(left) && Boolean(right) && left.trim().toLowerCase() === right.trim().toLowerCase();
}

/**
 * Place a run into its project, test cycle, and scenario, creating any that are missing.
 * Metadata in the file wins; the supplied context is used only as a fallback.
 * @param {Object} view - Evaluator view to capture.
 * @param {Object} [context] - Current project, cycle, and scenario ids.
 * @returns {Object} Updated workspace and the ids the run landed in.
 */
export function ingestRun(view, context = {}) {
  const meta = view.meta || {};
  let projects = loadProjects();

  let project = (meta.projectExternalId && projects.find((item) => item.externalId === meta.projectExternalId))
    || (meta.projectName && projects.find((item) => sameName(item.name, meta.projectName)))
    || (context.projectId && projects.find((item) => item.id === context.projectId))
    || null;

  const createdProject = !project;
  if (!project) {
    projects = createProject({
      name: meta.projectName || 'Untitled project',
      description: meta.projectDescription || '',
      externalId: meta.projectExternalId,
    });
    project = projects[projects.length - 1];
  }

  const cycleName = meta.testCycle || 'Cycle 1';
  const projectCycles = () => projects.find((item) => item.id === project.id).cycles;

  let cycle = projectCycles().find((item) => sameName(item.name, cycleName))
    || (!meta.testCycle && context.cycleId
      ? projectCycles().find((item) => item.id === context.cycleId)
      : null);

  const createdCycle = !cycle;
  if (!cycle) {
    projects = createCycle(project.id, { name: cycleName });
    cycle = projectCycles().slice(-1)[0];
  }

  const scenarioName = meta.scenarioName || 'Untitled scenario';
  const cycleScenarios = () => projectCycles().find((item) => item.id === cycle.id).scenarios;

  let scenario = (meta.scenarioExternalId
    && cycleScenarios().find((item) => item.externalId === meta.scenarioExternalId))
    || (meta.scenarioName && cycleScenarios().find((item) => sameName(item.name, meta.scenarioName)))
    || (!meta.scenarioName && context.scenarioId
      ? cycleScenarios().find((item) => item.id === context.scenarioId)
      : null)
    || null;

  const createdScenario = !scenario;
  if (!scenario) {
    projects = createScenario(project.id, cycle.id, {
      name: scenarioName,
      externalId: meta.scenarioExternalId,
    });
    scenario = cycleScenarios().slice(-1)[0];
  }

  const { projects: updated, run } = addRun(project.id, cycle.id, scenario.id, view);

  return {
    projects: updated,
    projectId: project.id,
    cycleId: cycle.id,
    scenarioId: scenario.id,
    runId: run.id,
    projectName: project.name,
    cycleName: cycle.name,
    scenarioName: scenario.name,
    created: { project: createdProject, cycle: createdCycle, scenario: createdScenario },
  };
}

/**
 * Rehydrate a stored run into a report the scoreboard can render.
 * @param {Object} run - Stored run.
 * @returns {Object} Evaluator-format report.
 */
export function runToReport(run) {
  return {
    format: 'evaluator',
    fileName: run.label,
    title: run.title,
    workspace: 'AI Evaluation',
    description: run.description,
    generatedAt: run.generatedAt,
    sources: run.sources,
    evaluators: run.evaluators,
    conversations: run.conversations || {},
    scenarioLayout: Boolean(run.scenarioLayout),
    summary: run.summary,
  };
}

/**
 * Summarize a scenario's run history.
 * @param {Object} scenario - Scenario record.
 * @returns {Object} Derived scenario statistics.
 */
export function scenarioStats(scenario) {
  const runs = scenario?.runs || [];
  const latestRun = runs[runs.length - 1] || null;
  const previousRun = runs[runs.length - 2] || null;
  const latestScore = latestRun ? latestRun.summary.overallAverage : null;
  const delta = latestRun && previousRun
    ? latestRun.summary.overallAverage - previousRun.summary.overallAverage
    : null;

  return {
    runCount: runs.length,
    latestRun,
    previousRun,
    latestScore,
    delta,
    belowTarget: latestRun ? latestRun.summary.belowTarget : 0,
  };
}

/**
 * Summarize a group of scenarios.
 * @param {Array<Object>} scenarios - Scenario records.
 * @returns {Object} Derived statistics.
 */
function summarizeScenarios(scenarios) {
  const allRuns = scenarios.flatMap((scenario) => scenario.runs || []);
  const latestScores = scenarios
    .map((scenario) => scenarioStats(scenario).latestScore)
    .filter((score) => score !== null && score !== undefined);
  const lastActivity = allRuns.reduce(
    (latest, run) => (!latest || run.importedAt > latest ? run.importedAt : latest),
    null,
  );

  return {
    scenarioCount: scenarios.length,
    runCount: allRuns.length,
    averageScore: latestScores.length
      ? latestScores.reduce((sum, score) => sum + score, 0) / latestScores.length
      : null,
    lastActivity,
  };
}

/**
 * Summarize a test cycle.
 * @param {Object} cycle - Cycle record.
 * @returns {Object} Derived cycle statistics.
 */
export function cycleStats(cycle) {
  return summarizeScenarios(cycle?.scenarios || []);
}

/**
 * Summarize a project across all of its cycles.
 * @param {Object} project - Project record.
 * @returns {Object} Derived project statistics.
 */
export function projectStats(project) {
  const cycles = project?.cycles || [];
  return {
    cycleCount: cycles.length,
    ...summarizeScenarios(cycles.flatMap((cycle) => cycle.scenarios || [])),
  };
}
